/* Keep the saved article intact. Move whole DOM blocks at a natural midpoint. */
(() => {
  async function randomiseProducts(promo) {
    if (promo.dataset.randomised) return;
    promo.dataset.randomised = 'loading';
    try {
      let url = new URL(promo.dataset.productsUrl, location.origin);
      const visited = new Set();
      const products = new Set();
      const selection = [];
      let eligible = 0;
      while (url) {
        if (url.origin !== location.origin || visited.has(url.href)) throw new Error('Invalid product pagination');
        visited.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('Product pool unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const pool = page.querySelector('template[data-product-pool]');
        if (!pool) throw new Error('Product pool missing');
        for (const card of pool.content.querySelectorAll('.hss-article-products__item')) {
          const handle = card.querySelector('a')?.getAttribute('href');
          if (!handle || products.has(handle)) continue;
          products.add(handle);
          eligible += 1;
          // Reservoir sampling gives every available product the same chance,
          // including products on later catalogue pages, without retaining them all.
          const index = Math.floor(Math.random() * eligible);
          if (selection.length < 4) selection.push(card);
          else if (index < 4) selection[index] = card;
        }
        url = pool.dataset.nextUrl ? new URL(pool.dataset.nextUrl, location.origin) : null;
        if (url) url.searchParams.set('section_id', 'hss-article-product-pool');
      }
      // Shuffle the selected four too, so mobile's first two are random.
      for (let i = selection.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [selection[i], selection[j]] = [selection[j], selection[i]];
      }
      promo.querySelector('.hss-article-products__grid').replaceChildren(...selection);
      promo.hidden = selection.length === 0;
      promo.dataset.randomised = 'true';
      promo.dataset.eligibleProducts = String(eligible);
    } catch {
      // Keep the server-rendered, currently available fallback on network failure.
      promo.dataset.randomised = 'fallback';
    }
  }
  function placeProducts(root) {
    root.querySelectorAll('[data-article-products]').forEach((promo) => {
      randomiseProducts(promo);
      if (promo.dataset.placed) return;
      const content = promo.previousElementSibling;
      if (!content?.classList.contains('article-template__content')) return;
      // Unwrap a single editorial container for articles saved with a wrapper.
      let source = content;
      while (source.children.length === 1 && /^(DIV|SECTION)$/.test(source.firstElementChild.tagName)) {
        source = source.firstElementChild;
      }
      const blocks = Array.from(source.children);
      if (blocks.length < 2) return; // A single indivisible block keeps the end-of-article fallback.
      const weights = blocks.map(block => Math.max(block.textContent.trim().length, block.querySelector('img,iframe,video') ? 350 : 0));
      const total = weights.reduce((sum, weight) => sum + weight, 0);
      let read = 0;
      const breaks = blocks.map((block, index) => {
        const point = { index, ratio: total ? read / total : index / blocks.length, heading: /^H[23]$/.test(block.tagName) };
        read += weights[index];
        return point;
      }).filter(point => point.index > 0 && !/^H[1-6]$/.test(blocks[point.index - 1].tagName));
      const headings = breaks.filter(point => point.heading && point.ratio >= .3 && point.ratio <= .7);
      const choices = headings.length ? headings : breaks;
      const split = choices.sort((a, b) => Math.abs(a.ratio - .5) - Math.abs(b.ratio - .5))[0];
      if (!split) return;
      const continuation = document.createElement('div');
      continuation.className = 'article-template__content hss-article-continuation page-width page-width--narrow rte';
      // Clone only the wrapper chain, preserving classes and inline editorial styles.
      let destination = continuation;
      const wrappers = [];
      for (let node = source; node !== content; node = node.parentElement) wrappers.unshift(node);
      wrappers.forEach(wrapper => {
        const clone = wrapper.cloneNode(false);
        clone.removeAttribute('id');
        destination.appendChild(clone);
        destination = clone;
      });
      let node = blocks[split.index];
      while (node) {
        const next = node.nextSibling;
        destination.appendChild(node);
        node = next;
      }
      promo.after(continuation);
      promo.dataset.placed = 'true';
    });
  }
  placeProducts(document);
  if (!window.hssArticleProductsBound) {
    document.addEventListener('shopify:section:load', event => placeProducts(event.target));
    window.hssArticleProductsBound = true;
  }
})();
